import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { FaArrowLeft } from "react-icons/fa";
import { Button } from "../../../components/ui";
import apiService from "../../../services/apiService";
import Loader from "../../../components/ui/Loader";
import { useToast } from "../../../context/ToastContext";

const createUploadApi = (qSetId) =>
  `/i/ims/tools/qSet/${qSetId}/upload-solution-pdf`;

const CHECK_STATUS_API = "/i/ims/tools/qSet/check-solution-conversion";

const getDataObject = (response) =>
  response?.data?.data || response?.data || response || {};

const isNoConversionStatus = (status) => {
  if (!status || typeof status !== "object") return true;

  const message = String(status.message || "")
    .trim()
    .toLowerCase();

  if (
    status.found === false ||
    message.includes("no solution conversion found") ||
    message.includes("solution conversion not found")
  ) {
    return true;
  }

  const hasJobId = Boolean(String(status.jobId || "").trim());
  const hasUploadStatus = Boolean(String(status.uploadStatus || "").trim());
  const isRunning = status.isRunning === true;

  return !hasJobId && !hasUploadStatus && !isRunning;
};

const getStageText = (activeStep) => {
  const step = Number(activeStep);
  if (step < 0) return "Conversion failed";
  if (step === 0) return "PDF conversion running";
  if (step === 1) return "PDF converted to text";
  if (step === 2) return "AI parsing solutions";
  if (step >= 3) return "Conversion completed";
  return "Conversion pending";
};

const getProgressPercent = (activeStep) => {
  const step = Number(activeStep);
  if (step < 0) return 100;
  if (step === 0) return 20;
  if (step === 1) return 50;
  if (step === 2) return 80;
  if (step >= 3) return 100;
  return 0;
};

const parseBoolean = (value) =>
  value === true ||
  value === 1 ||
  ["true", "1", "yes", "on"].includes(
    String(value ?? "")
      .trim()
      .toLowerCase(),
  );

const UploadSolutionNew = () => {
  const navigate = useNavigate();
  const { id: qSetId } = useParams();
  const pollTimerRef = useRef(null);
  const { showSuccess, showError, showInfo, showWarning } = useToast();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      totalSolutions: "",
      solutionStart: 1,
      solutionEnd: "",
      layoutType: "AUTO",
      translateMissingLanguage: false,
    },
  });

  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [conversionStatus, setConversionStatus] = useState(null);

  const totalSolutions = Number(watch("totalSolutions") || 0);
  const solutionStart = Number(watch("solutionStart") || 1);
  const solutionEnd = Number(watch("solutionEnd") || 0);
  const translateMissingLanguage = parseBoolean(
    watch("translateMissingLanguage"),
  );
  const selectedLayoutType = watch("layoutType") || "AUTO";

  useEffect(() => {
    checkConversionStatus(false).catch(() => null);

    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [qSetId]);

  const checkConversionStatus = async (showRunningWarning = false) => {
    if (!qSetId) {
      setConversionStatus(null);
      return null;
    }

    const response = await apiService.request(
      `${CHECK_STATUS_API}?qSetId=${encodeURIComponent(qSetId)}`,
      { method: "GET" },
    );

    const status = getDataObject(response);

    if (isNoConversionStatus(status)) {
      setConversionStatus(null);
      return null;
    }

    setConversionStatus(status);

    if (showRunningWarning && status?.isRunning) {
      showWarning(
        `Solution conversion already running. Stage: ${
          status.message || getStageText(status.activeStep)
        }`,
      );
    }

    return status;
  };

  const pollConversionStatus = async () => {
    if (!qSetId) return;

    try {
      setPolling(true);
      const status = await checkConversionStatus(false);
      if (!status) {
        setPolling(false);
        return;
      }

      const activeStep = Number(status.activeStep);
      const uploadStatus = String(status?.uploadStatus || "");

      if (activeStep >= 3 || activeStep < 0 || uploadStatus === "Failed") {
        setPolling(false);

        if (pollTimerRef.current) {
          clearTimeout(pollTimerRef.current);
          pollTimerRef.current = null;
        }

        if (activeStep >= 3 && uploadStatus !== "Failed") {
          showSuccess(status?.message || "Solution conversion completed");
        } else {
          showError(status?.message || "Solution conversion failed");
        }

        return;
      }

      pollTimerRef.current = setTimeout(pollConversionStatus, 10000);
    } catch (error) {
      console.error("SOLUTION STATUS ERROR:", error);
      setPolling(false);
    }
  };

  const handleSubmitCustom = async (data) => {
    const file = data.file?.[0];
    const finalTotal = Number(data.totalSolutions || 0);
    const finalStart = Number(data.solutionStart || 1);
    const finalEnd = Number(data.solutionEnd || finalTotal || 0);
    const shouldTranslate = parseBoolean(data.translateMissingLanguage);

    if (!qSetId) return showError("QSet ID not found.");
    if (!file) return showWarning("Please select a PDF file.");
    if (file.type && file.type !== "application/pdf") {
      return showWarning("Only PDF file is allowed.");
    }
    if (!finalTotal || finalTotal <= 0) {
      return showWarning("Please enter total solutions.");
    }
    if (!finalStart || finalStart < 1) {
      return showWarning("Solution start must be at least 1.");
    }
    if (!finalEnd || finalEnd < finalStart) {
      return showWarning(
        "Solution end must be greater than or equal to start.",
      );
    }
    if (finalEnd - finalStart + 1 !== finalTotal) {
      return showWarning(
        `Range count must be exactly ${finalTotal}. Current count: ${
          finalEnd - finalStart + 1
        }`,
      );
    }

    try {
      setLoading(true);

      const runningStatus = await checkConversionStatus(false).catch(
        () => null,
      );

      if (runningStatus?.isRunning) {
        showWarning(
          `Solution conversion already running. Stage: ${
            runningStatus.message || getStageText(runningStatus.activeStep)
          }`,
        );
        return;
      }

      const formData = new FormData();

      formData.append("pdfFile", file);
      formData.append("qSet", String(qSetId));
      formData.append("totalSolutions", String(finalTotal));
      formData.append("solutionStart", String(finalStart));
      formData.append("solutionEnd", String(finalEnd));
      formData.append("layoutType", String(data.layoutType || "AUTO"));
      formData.append("translateMissingLanguage", String(shouldTranslate));

      const queryParams = new URLSearchParams();

      queryParams.append("qSet", String(qSetId));
      queryParams.append("totalSolutions", String(finalTotal));
      queryParams.append("solutionStart", String(finalStart));
      queryParams.append("solutionEnd", String(finalEnd));
      queryParams.append("layoutType", String(data.layoutType || "AUTO"));
      queryParams.append("translateMissingLanguage", String(shouldTranslate));

      const uploadUrl = `${createUploadApi(qSetId)}?${queryParams.toString()}`;

      console.log("SOLUTION UPLOAD URL:", uploadUrl);

      const response = await apiService.request(uploadUrl, {
        method: "POST",
        body: formData,
      });

      const uploadData = getDataObject(response);

      if (uploadData?.alreadyRunning || uploadData?.isRunning) {
        setConversionStatus(uploadData);
        showWarning(
          `Solution conversion already running. Stage: ${
            uploadData.message || getStageText(uploadData.activeStep)
          }`,
        );
        return;
      }

      setConversionStatus({
        isRunning: true,
        jobId: uploadData?.jobId,
        activeStep: Number(uploadData?.activeStep ?? 0),
        uploadStatus: uploadData?.uploadStatus || "Uploading",
        message: uploadData?.message || "Solution PDF upload started",
        translateMissingLanguage: shouldTranslate,
        layoutType: data.layoutType || "AUTO",
      });

      showInfo("Solution PDF upload started.");
      showSuccess("File received. Conversion has started.");
      pollConversionStatus();
    } catch (error) {
      console.error("SOLUTION UPLOAD ERROR:", error);
      showError(error?.message || "Solution upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const progressPercent =
    conversionStatus && !isNoConversionStatus(conversionStatus)
      ? getProgressPercent(conversionStatus.activeStep)
      : 0;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Upload Solutions</h1>
        <Button
          onClick={() => navigate("/tools/qSet")}
          variant="light"
          size="sm"
          icon={FaArrowLeft}
        >
          Back to List
        </Button>
      </div>

      {conversionStatus && !isNoConversionStatus(conversionStatus) && (
        <div
          className={`mb-5 rounded-lg border p-4 ${
            Number(conversionStatus.activeStep) < 0
              ? "border-red-300 bg-red-50"
              : Number(conversionStatus.activeStep) >= 3
                ? "border-green-300 bg-green-50"
                : "border-yellow-300 bg-yellow-50"
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold">
                {conversionStatus.message ||
                  getStageText(conversionStatus.activeStep)}
              </p>
              <p className="mt-1 text-sm">
                Stage: {getStageText(conversionStatus.activeStep)}
              </p>
              {conversionStatus.jobId && (
                <p className="mt-1 break-all text-xs">
                  Job ID: {conversionStatus.jobId}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => checkConversionStatus(false)}
              className="rounded bg-gray-800 px-3 py-1 text-sm text-white"
            >
              Refresh
            </button>
          </div>

          <div className="mt-3">
            <div className="mb-1 flex justify-between text-xs">
              <span>Progress</span>
              <span>{progressPercent}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-green-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {polling && (
            <p className="mt-2 text-xs text-blue-700">
              Status auto refresh is running...
            </p>
          )}
        </div>
      )}

      <div className="mb-6 overflow-hidden rounded-lg bg-white shadow-md">
        <form onSubmit={handleSubmit(handleSubmitCustom)} className="p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-gray-700">
                Total Solutions <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                className="w-full rounded border p-2"
                disabled={loading}
                {...register("totalSolutions", {
                  required: "Total solutions is required",
                  min: { value: 1, message: "Minimum 1 solution required" },
                  onChange: (event) => {
                    const value = Number(event.target.value || 0);
                    // solutionEnd ko useForm automatically update nahi karta;
                    // user manually edit kar sakta hai.
                    if (!solutionEnd && value > 0) {
                      // intentionally no direct setValue dependency required
                    }
                  },
                })}
              />
              {errors.totalSolutions && (
                <p className="text-sm text-red-500">
                  {errors.totalSolutions.message}
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-gray-700">
                PDF Layout / Format <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full rounded border p-2"
                disabled={loading}
                {...register("layoutType", { required: true })}
              >
                <option value="AUTO">Auto detect</option>
                <option value="SINGLE_COLUMN">Single column</option>
                <option value="TWO_COLUMN">Two columns</option>
                <option value="MULTI_COLUMN">Multiple columns</option>
                <option value="SCANNED_IMAGE">Scanned / image PDF</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Current: {selectedLayoutType}
              </p>
            </div>

            <div>
              <label className="mb-2 block text-gray-700">
                Solution Start <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                className="w-full rounded border p-2"
                disabled={loading}
                {...register("solutionStart", {
                  required: "Solution start is required",
                  min: { value: 1, message: "Minimum value is 1" },
                })}
              />
              {errors.solutionStart && (
                <p className="text-sm text-red-500">
                  {errors.solutionStart.message}
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-gray-700">
                Solution End <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                className="w-full rounded border p-2"
                disabled={loading}
                placeholder={totalSolutions ? String(totalSolutions) : ""}
                {...register("solutionEnd", {
                  required: "Solution end is required",
                  min: { value: 1, message: "Minimum value is 1" },
                })}
              />
              {errors.solutionEnd && (
                <p className="text-sm text-red-500">
                  {errors.solutionEnd.message}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Current range count:{" "}
                {solutionStart && solutionEnd >= solutionStart
                  ? solutionEnd - solutionStart + 1
                  : 0}
              </p>
            </div>
          </div>

          <div className="mt-4 rounded border p-3">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1"
                disabled={loading}
                {...register("translateMissingLanguage")}
              />
              <span>
                <span className="block font-medium">
                  Translate missing solution language
                </span>
                <span className="block text-xs text-gray-500">
                  Checked: English-only solution also creates Hindi, and
                  Hindi-only solution also creates English. Unchecked: only
                  source language is saved.
                </span>
              </span>
            </label>
            <p className="mt-2 text-xs text-gray-500">
              Current: translation{" "}
              {translateMissingLanguage ? "enabled" : "disabled"}
            </p>
          </div>

          <div className="mt-4">
            <label className="mb-2 block text-gray-700">
              Select Solution PDF <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept=".pdf,application/pdf"
              disabled={loading}
              {...register("file", { required: "PDF file is required" })}
            />
            {errors.file && (
              <p className="text-sm text-red-500">{errors.file.message}</p>
            )}
          </div>

          <div className="mt-5 flex items-center gap-3">
            <button
              type="submit"
              disabled={loading}
              className={`rounded px-4 py-2 text-white ${
                loading
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-green-500 hover:bg-green-600"
              }`}
            >
              {loading ? "Uploading..." : "Upload"}
            </button>
            {loading && <Loader />}
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadSolutionNew;
